import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { UserPlus, Check } from 'lucide-react';
import '../nativeinventory/NativeInventoryAdjustmentNew.css';
import '../nativeitems/NativeItems.css';

// New Customer / New Vendor (Native) — native row first, NetSuite best-effort.
// Customers are template-based (NS requires category / terms / price level /
// retailer type — copied from a similar existing customer, like item create).
// Vendors need only a name. ?copyFrom=<id> prefills from an existing record
// and uses it as the template (Make Copy).

const CFG = {
  customers: {
    title: 'New Customer (Native)',
    api: '/api/native-records/customers',
    search: async (q) => {
      const { data } = await axios.get('/api/native-orders/customers', { params: { q } });
      return (data.customers || []).map((c) => ({ key: c.nsCustomerId || c.id, label: c.name, sub: c.email }));
    },
    recordPath: (r) => `/records/customers/${r.nsInternalId || r.id}`,
    needsTemplate: true,
  },
  vendors: {
    title: 'New Vendor (Native)',
    api: '/api/native-records/vendors',
    search: null,
    recordPath: (r) => `/records/vendors/${r.nsInternalId || r.id}`,
    needsTemplate: false,
  },
};

export default function NativeEntityNew({ kind }) {
  const cfg = CFG[kind];
  const [searchParams] = useSearchParams();
  const [template, setTemplate] = useState(null);
  const [tplQuery, setTplQuery] = useState('');
  const [tplResults, setTplResults] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Make Copy: prefill from the source record; the source is also the template.
  useEffect(() => {
    const copyFrom = searchParams.get('copyFrom');
    if (!copyFrom) return;
    axios.get(`/api/native-records/${kind}/${encodeURIComponent(copyFrom)}`)
      .then(({ data }) => {
        const r = data.record || {};
        setForm({
          name: '',
          email: r.email || '',
          phone: r.phone || '',
        });
        if (cfg.needsTemplate) {
          setTemplate({ key: r.ns_internal_id || r.id, label: r.company_name || r.name });
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!cfg.needsTemplate || !cfg.search || tplQuery.trim().length < 2 || template) { setTplResults([]); return undefined; }
    const t = setTimeout(async () => {
      try { setTplResults(await cfg.search(tplQuery.trim())); } catch { setTplResults([]); }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tplQuery, template]);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const body = kind === 'customers'
        ? { companyName: form.name.trim(), email: form.email.trim() || undefined, phone: form.phone.trim() || undefined, templateKey: String(template.key) }
        : { name: form.name.trim(), email: form.email.trim() || undefined, phone: form.phone.trim() || undefined };
      const { data } = await axios.post(cfg.api, body);
      setConfirmation(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    const rec = confirmation.customer || confirmation.vendor;
    return (
      <div className="nia-page">
        <div className="nia-card nia-confirm">
          <div className="nia-confirm-icon"><Check size={28} /></div>
          <h2>{rec.companyName || rec.name} created</h2>
          <div className="nia-confirm-grid">
            <span>NetSuite</span>
            <strong>{confirmation.nsSync === 'synced' ? `created (${rec.nsInternalId})` : `pending — ${confirmation.nsError || 'retry when NS is reachable'}`}</strong>
          </div>
          <div className="nia-actions">
            <Link className="nia-btn nia-btn-primary" to={cfg.recordPath(rec)} style={{ textDecoration: 'none' }}>Open record</Link>
            <button className="nia-btn nia-btn-ghost" onClick={() => window.location.reload()}>New</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="nia-page">
      <div className="nia-header">
        <h1><UserPlus size={20} /> {cfg.title}</h1>
      </div>

      {cfg.needsTemplate && (
        <div className="nia-card">
          <h3>1. Template — a similar existing customer</h3>
          <p className="nia-hint">The new customer copies the template's category, terms, price level, and retailer type (NetSuite-required).</p>
          {!template ? (
            <>
              <input className="nia-input nia-input-search" placeholder="Search customers…" value={tplQuery} onChange={(e) => setTplQuery(e.target.value)} />
              {tplResults.length > 0 && (
                <div className="nia-results">
                  {tplResults.map((r) => (
                    <button key={r.key} className="nia-result" onClick={() => { setTemplate(r); setTplQuery(''); setTplResults([]); }}>
                      <strong>{r.label}</strong>{r.sub && <em>{r.sub}</em>}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="nia-chip">
              {template.label}
              <button className="nia-icon-btn" onClick={() => setTemplate(null)}>×</button>
            </div>
          )}
        </div>
      )}

      <div className="nia-card">
        <h3>{cfg.needsTemplate ? '2. ' : ''}Details</h3>
        <div className="nit-form" style={{ maxWidth: 480 }}>
          <label><span>{kind === 'customers' ? 'Company name' : 'Vendor name'}</span><input value={form.name} onChange={set('name')} maxLength={200} /></label>
          <label><span>Email (optional)</span><input value={form.email} onChange={set('email')} maxLength={200} /></label>
          <label><span>Phone (optional)</span><input value={form.phone} onChange={set('phone')} maxLength={40} /></label>
        </div>
        {error && <div className="nia-error" style={{ marginTop: 10 }}>{error}</div>}
        <div className="nia-actions" style={{ marginTop: 12 }}>
          <button
            className="nia-btn nia-btn-primary"
            onClick={submit}
            disabled={submitting || !form.name.trim() || (cfg.needsTemplate && !template)}
          >
            {submitting ? 'Creating…' : 'Create'}
          </button>
        </div>
        <p className="nia-hint" style={{ marginTop: 8 }}>Creates natively first, then in NetSuite.</p>
      </div>
    </div>
  );
}
